import { z } from 'zod';
import { machineSchema } from './machine.js';

// ── Role enum ─────────────────────────────────────────────────────────────────

export const USER_ROLES = ['manager', 'programmer', 'operator'] as const;
export type UserRole = (typeof USER_ROLES)[number];

// ── DynamoDB record: shop user ─────────────────────────────────────────────────

export const shopUserRecordSchema = z.object({
  PK:           z.string(),
  SK:           z.string(),
  GSI1PK:       z.string(),
  GSI1SK:       z.string(),
  entity_type:  z.literal('SHOP_USER'),
  user_id:      z.string(),
  shop_id:      z.string(),
  email:        z.string().email(),
  display_name: z.string().nullable(),
  role:         z.enum(USER_ROLES),
  avatar_url:   z.string().nullable(),
  is_active:    z.boolean(),
  invited_by:   z.string().nullable(),
  invited_at:   z.string().datetime({ offset: true }).nullable(),
  joined_at:    z.string().datetime({ offset: true }).nullable(),
  last_seen_at: z.string().datetime({ offset: true }).nullable(),
  created_at:   z.string().datetime({ offset: true }),
  updated_at:   z.string().datetime({ offset: true }),
});

export type ShopUserRecord = z.infer<typeof shopUserRecordSchema>;

// ── DynamoDB record: machine assignment ───────────────────────────────────────

export const machineAssignmentRecordSchema = z.object({
  PK:           z.string(),
  SK:           z.string(),
  GSI1PK:       z.string(),
  GSI1SK:       z.string(),
  GSI2PK:       z.string(),
  GSI2SK:       z.string(),
  entity_type:  z.literal('MACHINE_ASSIGNMENT'),
  shop_id:      z.string(),
  machine_id:   z.string(),
  user_id:      z.string(),
  role_context: z.enum(USER_ROLES),
  assigned_by:  z.string(),
  assigned_at:  z.string().datetime({ offset: true }),
  is_primary:   z.boolean(),
  shift:        z.enum(['day', 'evening', 'night', 'all']).nullable(),
  notes:        z.string().nullable(),
});

export type MachineAssignmentRecord = z.infer<typeof machineAssignmentRecordSchema>;

// ── DynamoDB record: team (optional grouping) ─────────────────────────────────

export const teamRecordSchema = z.object({
  PK:          z.string(),
  SK:          z.string(),
  entity_type: z.literal('TEAM'),
  team_id:     z.string().uuid(),
  shop_id:     z.string(),
  name:        z.string(),
  machine_ids: z.array(z.string()),
  user_ids:    z.array(z.string()),
  created_by:  z.string(),
  created_at:  z.string().datetime({ offset: true }),
  updated_at:  z.string().datetime({ offset: true }),
});

export type TeamRecord = z.infer<typeof teamRecordSchema>;

// ── DynamoDB record: user invitation ─────────────────────────────────────────

export const userInvitationRecordSchema = z.object({
  PK:          z.string(),
  SK:          z.string(),
  entity_type: z.literal('INVITATION'),
  invite_id:   z.string().uuid(),
  shop_id:     z.string(),
  email:       z.string().email(),
  role:        z.enum(USER_ROLES),
  machine_ids: z.array(z.string()),
  invited_by:  z.string(),
  invited_at:  z.string().datetime({ offset: true }),
  expires_at:  z.string().datetime({ offset: true }),
  accepted_at: z.string().datetime({ offset: true }).nullable(),
  status:      z.enum(['pending', 'accepted', 'expired']),
});

export type UserInvitationRecord = z.infer<typeof userInvitationRecordSchema>;

// ── API response schemas ──────────────────────────────────────────────────────

export const assignedMachineResponseSchema = z.object({
  machine_id:   z.string(),
  role_context: z.enum(USER_ROLES),
  is_primary:   z.boolean(),
  shift:        z.string().nullable(),
});

export type AssignedMachineResponse = z.infer<typeof assignedMachineResponseSchema>;

export const shopUserResponseSchema = z.object({
  user_id:           z.string(),
  email:             z.string(),
  display_name:      z.string().nullable(),
  role:              z.enum(USER_ROLES),
  avatar_url:        z.string().nullable(),
  is_active:         z.boolean(),
  last_seen_at:      z.string().nullable(),
  assigned_machines: z.array(assignedMachineResponseSchema),
  joined_at:         z.string().nullable(),
});

export type ShopUserResponse = z.infer<typeof shopUserResponseSchema>;

export const machineWithAssignmentsResponseSchema = machineSchema.extend({
  assigned_users: z.object({
    operators:   z.array(shopUserResponseSchema),
    programmers: z.array(shopUserResponseSchema),
  }),
});

export type MachineWithAssignmentsResponse = z.infer<typeof machineWithAssignmentsResponseSchema>;

export const inviteResponseSchema = z.object({
  invite_id:   z.string(),
  email:       z.string(),
  expires_at:  z.string(),
  role:        z.enum(USER_ROLES),
  shop_id:     z.string().optional(),
  machine_ids: z.array(z.string()).optional(),
  status:      z.enum(['pending', 'accepted', 'expired']).optional(),
});

export type InviteResponse = z.infer<typeof inviteResponseSchema>;
